import { getStaticFormFields } from './staticFieldsAdapter';
import { normalizeFieldForUi } from '../utils/fieldTypeAdapter';

const cloneValue = (value) => {
  if (Array.isArray(value)) return value.map(cloneValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, cloneValue(item)]));
  }
  return value;
};

const isForm = (value) =>
  Boolean(
    value &&
      typeof value === 'object' &&
      (value.leads_form_items || value.leads_form_name || value.leads_form_id)
  );

function findForm(value, formId, depth = 0) {
  if (!value || depth > 6) return null;
  if (isForm(value)) return value;

  if (Array.isArray(value)) {
    const exact = formId
      ? value.find(
          (item) =>
            String(item?.leads_form_id ?? item?.id ?? item?._id ?? '') === String(formId)
        )
      : null;
    if (exact) return exact;
    for (const item of value) {
      const found = findForm(item, formId, depth + 1);
      if (found) return found;
    }
    return null;
  }

  if (typeof value === 'object') {
    if (formId && value[formId]) {
      const exact = findForm(value[formId], formId, depth + 1);
      if (exact) return exact;
    }
    for (const item of Object.values(value)) {
      const found = findForm(item, formId, depth + 1);
      if (found) return found;
    }
  }
  return null;
}

export function sortFormFieldsByOrder(fields = []) {
  const list = Array.isArray(fields) ? [...fields] : Object.values(fields || {});
  return list.filter(Boolean).sort((a, b) => {
    const orderA = a?.order_id != null && a?.order_id !== '' ? Number(a.order_id) : (Number(a?.item_id) || 0);
    const orderB = b?.order_id != null && b?.order_id !== '' ? Number(b.order_id) : (Number(b?.item_id) || 0);
    if (!Number.isNaN(orderA) && !Number.isNaN(orderB) && orderA !== orderB) {
      return orderA - orderB;
    }
    return (Number(a?.item_id) || 0) - (Number(b?.item_id) || 0);
  });
}

export function cloneFormFields(fields) {
  const values = Array.isArray(fields) ? fields : Object.values(fields || {});
  const cloned = values.filter(Boolean).map(cloneValue);
  return sortFormFieldsByOrder(cloned);
}

export function injectStaticFormFields(fields, staticFields = getStaticFormFields()) {
  const cloned = cloneFormFields(fields);
  const existingKeys = new Set(cloned.map((field) => field?.key_name));
  const missing = staticFields
    .filter((field) => !existingKeys.has(field.key_name))
    .map(cloneValue);
  const leadId = missing.find((field) => field.key_name === 'lead_id');
  const remaining = missing.filter((field) => field.key_name !== 'lead_id');
  const leadIdField = leadId ? { ...leadId, order_id: 0 } : null;
  const combined = leadIdField ? [leadIdField, ...cloned, ...remaining] : [...cloned, ...remaining];

  let nextId = combined.reduce(
    (max, field) => Math.max(max, Number(field?.item_id) || 0),
    0
  );
  const mapped = combined.map((field, index) => ({
    ...field,
    order_id: field.order_id ?? index + 1,
    item_id: field.item_id ?? ++nextId,
  }));
  return sortFormFieldsByOrder(mapped);
}

export function hydrateLegacyFormResponse(response, options = {}) {
  const source = findForm(response, options.formId);
  if (!source) return null;

  const hydrated = cloneValue(source);
  const rawFields = source.leads_form_items ?? source.items ?? source.fields ?? {};
  const fields = options.injectStatic === false
    ? cloneFormFields(rawFields)
    : injectStaticFormFields(rawFields, options.staticFields || getStaticFormFields());
  hydrated.leads_form_items = sortFormFieldsByOrder(fields.map((field) => {
    const statusOption =
      field.statusOption ??
      (field.is_status ? 'is_status' : field.is_sub_status ? 'is_sub_status' : '');
    const { is_status, is_sub_status, ...contractField } = field;
    return normalizeFieldForUi({
      ...contractField,
      groupId: contractField.groupId ?? '',
      statusOption,
      status_parent_key: contractField.status_parent_key ?? '',
      is_encrypted: contractField.is_encrypted ?? false,
    });
  }));
  hydrated.use_for_ai =
    source.use_for_ai === true || source.use_for_ai === 1 || source.use_for_ai === '1' ? 1 : 0;
  hydrated.group_options = cloneValue(source.group_options || []);
  hydrated.templates = cloneValue(source.templates || []);
  hydrated.global_hot_lead_criteria = cloneValue(source.global_hot_lead_criteria || []);
  return hydrated;
}

export { getStaticFormFields } from './staticFieldsAdapter';
